import { inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import { QuestionService } from '@core/ask-hr-dynamic-forms-smart-ui';
import type {
  DefineFormData,
  DynamicFormConfig,
  RespondFormWrapperConfig,
  UnwrapSignal,
} from '@core/ask-hr-dynamic-forms-types';
import type { LinkTemplateItem } from '@core/ask-hr-templates-types';
import { URL_PROTOCOL_PATTERN, urlValidator } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';

type DefineComponent = import('@core/ask-hr-dynamic-forms-smart-ui').QuestionComponent;
type RespondComponent = import('@core/ask-hr-dynamic-forms-smart-ui').RespondTextComponent;
type PresentComponent = import('@core/ask-hr-dynamic-forms-smart-ui').TextPresenterComponent;

export class LinkConfig implements DynamicFormConfig {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly questionService = inject(QuestionService);
  private readonly i18n = inject(I18nService);

  public readonly kind: LinkTemplateItem['type'] = 'link';
  public readonly defineConfig: DynamicFormConfig['defineConfig'] = {
    name: this.i18n.translate('tlc.ask_hr.form_template_create.configs.link'),
    icon: 'link',
  };

  public readonly components = {
    define: () => import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.QuestionComponent),
    respond: () => import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.RespondTextComponent),
    present: () =>
      import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.TextPresenterComponent),
  };

  public readonly defineForm = (
    data?: DefineFormData,
  ): UnwrapSignal<DefineComponent['defineForm']> => this.questionService.createQuestionForm(data);

  public readonly respondConfig = (
    data: RespondFormWrapperConfig,
  ): UnwrapSignal<RespondComponent['respondConfig']> => ({
    ...data,
    kind: 'single-line',
    placeholder: this.i18n.translate('tlc.ask_hr.form_template_create.configs.respond_placeholder'),
    errors: {
      url: this.i18n.translate('tlc.ask_hr.form_template_create.errors.url_invalid'),
    },
    onBlur: (fromGroup: UnwrapSignal<RespondComponent['respondForm']>) => {
      const value = fromGroup.getRawValue().value;
      if (!value) return;

      const hasProtocol = URL_PROTOCOL_PATTERN.test(value);
      if (hasProtocol) return;

      fromGroup.controls.value.setValue(`https://${value}`);
    },
  });

  public readonly respondForm = (config: {
    required: boolean;
  }): UnwrapSignal<RespondComponent['respondForm']> =>
    this.formBuilder.group({
      value: this.formBuilder.control<string | null>('', [
        ...(config.required ? [Validators.required] : []),
        urlValidator,
      ]),
    });

  public readonly presentConfig = (
    data: LinkTemplateItem,
  ): UnwrapSignal<PresentComponent['config']> => ({
    heading: data.name,
    value: data.value,
    kind: {
      type: 'link',
    },
  });
}
